import React, { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import BodyText from 'LuxKit/Typography/BodyText'
import { setGeoRedirect, Status } from 'storage/geo'
import { connect } from 'react-redux'
import setRegionCookie from 'cookies/setRegionCookie'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import TextButton from 'LuxKit/Button/TextButton'
import Flag from 'LuxKit/Flag'
import ModalBody from 'LuxKit/Modal/ModalBody'
import type { RouterLocation } from 'connected-react-router'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import config from 'constants/config'
import TextLink from 'LuxKit/TextLink'
import { agentHubOrSubBrandConfig } from 'agentHub/constants'

interface Props {
  accountRegionCode: string
  currentRegionCode: string
  location: RouterLocation<unknown>
}

function GeoRedirectModal(props: Props) {
  const { accountRegionCode, currentRegionCode, location } = props
  const intl = useIntl()
  const modalContext = useModalElementContext()

  const accountRegion = useMemo(() => config.regions.find(region => region.code === accountRegionCode)!, [accountRegionCode])
  const currentRegion = useMemo(() => config.regions.find(region => region.code === currentRegionCode)!, [currentRegionCode])
  const onRegionChange = useCallback(() => {
    setRegionCookie(accountRegionCode)
    setGeoRedirect(Status.redirected)
    modalContext.resolve()
    window.location.href = location.pathname.replace(/^\/[a-z]+/, '/' + accountRegionCode.toLowerCase()) + location.search
  }, [accountRegionCode, location, modalContext])

  const onIgnoreClick = useCallback(() => {
    setRegionCookie(currentRegionCode)
    setGeoRedirect(Status.ignore)
    modalContext.resolve()
  }, [currentRegionCode, modalContext])

  const agentHubEnabled = config.agentHub.isEnabled

  const wrongPlaceTitle = intl.formatMessage({
    id: '9A+bj0m+2P',
    defaultMessage: 'It looks like you’re in the wrong place',
    description: 'Title for geo redirect modal when agent hub is enabled, telling user they are on the wrong regional site',
  })
  const rightPlaceTitle = intl.formatMessage({
    id: 'pFcy9mlfOL',
    defaultMessage: 'Are you in the right place?',
    description: 'Title for geo redirect modal asking user if they are on the correct regional site',
  })

  const bodyText = intl.formatMessage(
    {
      id: 'Zt+k1W+OmI',
      defaultMessage: 'Looks like you’re based in {regionName}, please select the correct site to browse exclusive offers in your local currency ({currencyCode}).',
      description: 'Body text in geo redirect modal explaining user should switch to their regional site',
    },
    { regionName: accountRegion.name, currencyCode: accountRegion.currencyCode },
  )

  const goToRegionLabel = intl.formatMessage(
    {
      id: 'Pos4pVCwDv',
      defaultMessage: 'Go to {regionName} site',
      description: 'Button label to navigate to the correct regional site',
    },
    { regionName: accountRegion.name },
  )

  const stayOnRegionLabel = intl.formatMessage(
    {
      id: 'fnq3hf0aCK',
      defaultMessage: 'Stay on {regionName} site',
      description: 'Button label to stay on the current regional site',
    },
    { regionName: currentRegion?.name },
  )

  const contactSupportText = intl.formatMessage({
    id: 'c577yltXOY',
    defaultMessage: 'If you think you’re in the right place, please contact our support team.',
    description: 'Message shown when agent hub is enabled, prompting user to contact support if they believe the geo redirect is incorrect',
  })

  return (
    <ModalBase
      size="S"
      dismissible={!agentHubEnabled}
      onClose={onIgnoreClick}
    >
      <ModalHeader
        dismissible={!agentHubEnabled}
        title={agentHubEnabled ? wrongPlaceTitle : rightPlaceTitle}
      />
      <ModalBody>
        <ModalContent>
          <BodyText variant="large">
            {bodyText}
          </BodyText>
        </ModalContent>
      </ModalBody>
      <ModalFooter actionsLayout="stacked">
        <TextButton
          startIcon={<Flag countryCode={accountRegion.code} />}
          kind="secondary"
          variant="dark"
          fit="full-width"
          size="large"
          onClick={onRegionChange}
        >
          {goToRegionLabel}
        </TextButton>
        {!agentHubEnabled && (
          <TextButton
            startIcon={<Flag countryCode={currentRegion?.code} />}
            kind="secondary"
            variant="dark"
            fit="full-width"
            size="large"
            data-testid="ignore-geo-redirect-modal"
            onClick={onIgnoreClick}
          >
            {stayOnRegionLabel}
          </TextButton>
        )}
        {agentHubEnabled && (
          <>
            <BodyText variant="medium">
              {contactSupportText}
            </BodyText>
            <TextLink href={`mailto:${agentHubOrSubBrandConfig.supportEmail}`}>{agentHubOrSubBrandConfig.supportEmail}</TextLink>
          </>
        )}
      </ModalFooter>
    </ModalBase>
  )
}

function mapStateToProps(state: App.State) {
  return {
    accountRegionCode: state.auth.account.countryCode,
    currentRegionCode: state.geo.currentRegionCode,
    location: state.router.location,
  }
}

export default connect(mapStateToProps)(GeoRedirectModal)
